/**
 * Zod schema for the `Attempt` persistence shape.
 *
 * Mirrors `../types/attempt.ts`. Spec authority: `architecture.md §4.7`
 * `CREATE TABLE attempts`, and `functionality.md §4.2` for the status machine.
 *
 * Wave 3 change: `in_progress` removed from the status enum (4-value union).
 * See migration 013 and `.plan/features/2026-05-11-cook-flow-mvp-amendment.md`.
 */

import { z } from "zod";
import type { Attempt, AttemptStatus } from "../types/attempt.js";
import { UUID_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const timestamp = z.number().int().nonnegative();

export const AttemptStatusSchema: z.ZodType<AttemptStatus> = z.enum([
  "draft",
  "cooked",
  "promoted",
  "archived",
]);

export const AttemptSchema = z
  .object({
    uuid,
    branchUuid: uuid,
    parentCommitUuid: uuid.nullable().optional(),
    bodyMarkdown: z.string(),
    status: AttemptStatusSchema,
    authorAgent: z.string(),
    batchUuid: uuid.nullable().optional(),
    ratingSchemaVersionUuid: uuid.nullable().optional(),
    cookTs: timestamp.nullable().optional(),
    createdAt: timestamp,
  })
  .strict();

type _Assert =
  z.infer<typeof AttemptSchema> extends Attempt
    ? Attempt extends z.infer<typeof AttemptSchema>
      ? true
      : never
    : never;
export type __AttemptSchemaAssert = _Assert;
